import { useMemo } from 'react';
import { Box, Button, Flex, Icon, SimpleGrid, Text, VStack } from '@chakra-ui/react';
import { LuArrowRight, LuCheck, LuUndo2 } from 'react-icons/lu';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../../utils/intlFormatters';
import { TONES, moneyText, offByText, statementFlags, summarySentences } from '../repairsUI';
import { cardSurface } from '../../shared/Card';
import { appliedSummary, itemKind, statementSums } from './checkupModel';
import { AMOUNT_COLOR, Tag, directedMoney, movementLabel } from './CheckupParts';
import { CheckupSheetFooter, PhoneNextFooter } from './PhoneCheckupSheet';
import { ReadTwicePhoneDone } from './ReadTwicePhone';

function Tile({ value, label }) {
  return (
    <Box bg="redesign.bg.card" borderRadius="10px" px={4} py={3}>
      <Text fontSize="xl" fontWeight={700} color="redesign.heading" sx={{ fontVariantNumeric: 'tabular-nums' }}>{value}</Text>
      <Text fontSize="xs" color="redesign.text.muted">{label}</Text>
    </Box>
  );
}

// One applied change, as a line: the movement and what happened to it.
function AppliedLine({ item, details, names, ctx }) {
  const { t } = useTranslation('repairs');
  const kind = itemKind(item);
  const change = item.change || {};
  const facts = (item.movements || []).find((movement) => movement.id === change.movement_id);
  const currency = change.currency || facts?.currency;
  let title;
  let right = null;
  if (['balance', 'housekeeping', 'other'].includes(kind) || (kind === 'new' && change.movement_id == null)) {
    const sentences = summarySentences(item.keep_lose_gain, t, ctx);
    title = kind === 'new' ? t('checkup.changes.newMovement') : (sentences.gain[0] || sentences.lose[0] || sentences.keep[0]);
    if (kind === 'new') right = <Tag tone="blue">{t('checkup.changes.new')}</Tag>;
  } else {
    title = movementLabel(details[change.movement_id], facts, names, t).title;
    if (kind === 'sign' || kind === 'amount') {
      right = (
        <Text fontSize="sm" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          <Text as="span" color="redesign.text.muted" textDecoration="line-through">{directedMoney(change.before, currency)}</Text>
          {' → '}
          <Text as="span" fontWeight={600} color={AMOUNT_COLOR[change.after?.direction]}>{directedMoney(change.after, currency)}</Text>
        </Text>
      );
    } else if (kind === 'date') {
      right = <Text fontSize="sm">{`${formatDate(change.before?.date)} → ${formatDate(change.after?.date)}`}</Text>;
    } else if (kind === 'gone') {
      right = <Tag tone="red">{t('checkup.changes.removed')}</Tag>;
    } else if (kind === 'leftover') {
      right = <Tag tone="muted">{t('checkup.readTwice.extraRemoved')}</Tag>;
    }
  }
  return (
    <Flex justify="space-between" align="center" gap={3} py={2.5} borderBottom="1px solid" borderColor="redesign.border" _last={{ borderBottom: 0 }}>
      <Box minW={0}>
        <Text fontSize="sm" fontWeight={600} color="redesign.heading" noOfLines={2}>{title}</Text>
        <Text fontSize="xs" color="redesign.text.muted">{t(`checkup.changes.kinds.${kind}`)}</Text>
      </Box>
      {right}
    </Flex>
  );
}

/**
 * A statement once its check is applied: whether it adds up now, what the
 * check looked at and what it changed, Undo, and the next statement of the
 * checkup. After an undo it says so and offers a new check. `alone` is a
 * statement checked on its own, outside the checkup's queue: it leads back
 * to the statements and to nothing else. On the phone the next statement
 * sits in the checkup sheet's footer.
 */
export default function CheckupDone({
  run, name, details, names, ctx, onUndo, undoing, onCheckAgain, next, onContinue, onBack, alone = false, isPhone = false,
}) {
  const { t } = useTranslation('repairs');
  const summary = useMemo(() => appliedSummary(run), [run]);
  const sums = useMemo(() => statementSums(run.statement_diff), [run]);
  const undone = run.status === 'undone';
  const addsUp = sums.length > 0 && sums.every((entry) => (entry.after || entry.before).addsUp !== false)
    && sums.some((entry) => (entry.after || entry.before).addsUp === true);
  const closing = sums.find((entry) => entry.printed && (entry.after || entry.before).closing != null);
  // A check that only removed extra readings: what it cleared, and that
  // nothing else changed.
  const twice = summary.readTwice && !undone;
  const readings = summary.applied.length;
  const twiceTiles = [
    ...(summary.warningsCleared > 0 ? [[summary.warningsCleared, t('checkup.readTwice.done.warnings')]] : []),
    [0, t('checkup.readTwice.done.movements')],
    [t('checkup.readTwice.tiles.noChange'), t('checkup.readTwice.done.balance')],
  ];
  const tiles = [
    [summary.alreadyRight, 'alreadyRight'],
    [summary.changed, 'changed'],
    [summary.added, 'added'],
    [summary.removed, 'removed'],
    [summary.other, 'other'],
  ].filter(([value], index) => value > 0 || (index === 0 && summary.checked > 0));

  const phoneFooter = isPhone && (
    <CheckupSheetFooter>
      <PhoneNextFooter next={next} alone={alone} onContinue={onContinue} onFinish={onBack} />
    </CheckupSheetFooter>
  );
  if (isPhone && twice) {
    return (
      <>
        <ReadTwicePhoneDone summary={summary} onUndo={onUndo} undoing={undoing} />
        {phoneFooter}
      </>
    );
  }

  return (
    <VStack align="stretch" spacing={5} maxW="680px" mx="auto" w="100%" py={[2, 4]}>
      <VStack spacing={3} textAlign="center">
        <Flex w="64px" h="64px" borderRadius="full" align="center" justify="center" {...(undone ? TONES.muted : TONES.green)}>
          <Icon as={undone ? LuUndo2 : LuCheck} boxSize="30px" color={undone ? 'redesign.text.muted' : 'phone.income'} />
        </Flex>
        <Text as="h1" fontSize={['lg', 'xl']} fontWeight={600} color="redesign.heading" sx={{ textWrap: 'balance' }}>
          {undone
            ? t('checkup.done.undone')
            : t(twice ? 'checkup.readTwice.done.title' : addsUp ? 'checkup.done.titleAddsUp' : 'checkup.done.titleFixed', { name })}
        </Text>
        {twice && (
          <Text fontSize="sm" color="redesign.text" maxW="560px">
            {t(summary.warningsCleared > 0 ? 'checkup.readTwice.done.subWarnings' : 'checkup.readTwice.done.sub')}
          </Text>
        )}
        {!undone && !twice && (
          <Text fontSize="sm" color="redesign.text">
            {addsUp && closing
              ? t('checkup.done.addsUpSub', {
                amount: moneyText(Math.abs((closing.after || closing.before).closing), closing.currency),
              })
              : t('checkup.done.fixedSub')}
          </Text>
        )}
      </VStack>

      {twice && (
        <SimpleGrid columns={[1, 3]} spacing={2} {...cardSurface} borderRadius="14px" p={[4, 5]}>
          {twiceTiles.map(([value, label]) => <Tile key={label} value={value} label={label} />)}
        </SimpleGrid>
      )}

      {!undone && !twice && (
        <Box {...cardSurface} borderRadius="14px" p={[4, 5]}>
          <Text fontSize="sm" fontWeight={600} color="redesign.heading" mb={3}>
            {summary.checked > 0
              ? t(summary.checked === 1 ? 'checkup.done.checkedOne' : 'checkup.done.checkedOther', { count: summary.checked })
              : t(summary.applied.length === 1 ? 'checkup.done.changesOne' : 'checkup.done.changesOther', { count: summary.applied.length })}
          </Text>
          {tiles.length > 0 && (
            <SimpleGrid columns={[2, Math.min(tiles.length, 4)]} spacing={2} mb={3}>
              {tiles.map(([value, key]) => <Tile key={key} value={value} label={t(`checkup.done.${key}`)} />)}
            </SimpleGrid>
          )}
          <Box>
            {summary.applied.map((item) => (
              <AppliedLine key={item.id} item={item} details={details} names={names} ctx={ctx} />
            ))}
          </Box>
          {summary.blocked > 0 && (
            <Text mt={3} fontSize="xs" color="phone.alertText">
              {t(summary.blocked === 1 ? 'checkup.done.blockedOne' : 'checkup.done.blockedOther', { count: summary.blocked })}
            </Text>
          )}
        </Box>
      )}

      <Flex align="center" justify="space-between" gap={4} direction={['column', 'row']}>
        <Text fontSize="xs" color="redesign.text.muted" maxW="470px">
          {undone ? null : twice
            ? t(readings === 1 ? 'checkup.readTwice.done.undoHintOne' : 'checkup.readTwice.done.undoHintOther', { count: readings })
            : t('checkup.done.undoHint')}
        </Text>
        {undone ? (
          <Button variant="outline" onClick={onCheckAgain}>{t('checkup.done.checkAgain')}</Button>
        ) : (
          <Button variant="outline" leftIcon={<LuUndo2 />} onClick={onUndo} isLoading={undoing}>{t('checkup.done.undo')}</Button>
        )}
      </Flex>

      <Box display={isPhone ? 'none' : undefined} {...cardSurface} borderRadius="14px" p={[4, 5]}>
        {next ? (
          <VStack align="stretch" spacing={3}>
            <Text textStyle="microLabel">
              {t(next.left === 1 ? 'checkup.done.upNextOne' : 'checkup.done.upNextOther', { count: next.left })}
            </Text>
            <Flex align="center" justify="space-between" gap={4} direction={['column', 'row']}>
              <Box minW={0} w="100%">
                <Text fontSize="md" fontWeight={600} color="redesign.heading" noOfLines={1}>{next.name}</Text>
                <Flex gap={2} wrap="wrap" mt={1}>
                  {statementFlags(next.flags).map(({ key, attention }) => (
                    <Tag key={key} tone={attention ? 'orange' : 'muted'}>{t(`flags.${key}`)}</Tag>
                  ))}
                  {(next.flags?.off_by || []).map((offBy) => (
                    <Text key={`${offBy.account_id}-${offBy.currency}`} fontSize="xs" color="phone.alertText">
                      {t('checkup.card.offBy', { amount: offByText(offBy) })}
                    </Text>
                  ))}
                </Flex>
              </Box>
              <Button colorScheme="blue" rightIcon={<LuArrowRight />} onClick={onContinue} w={['100%', 'auto']} flexShrink={0}>
                {t('checkup.done.continue')}
              </Button>
            </Flex>
            <Text fontSize="xs" color="redesign.text.muted">{t('checkup.done.stopAnyTime')}</Text>
          </VStack>
        ) : (
          <Flex align="center" justify={alone ? 'flex-end' : 'space-between'} gap={4} direction={['column', 'row']}>
            {!alone && <Text fontSize="sm" color="redesign.text">{t('checkup.done.allDone')}</Text>}
            <Button variant="outline" onClick={onBack}>{t('checkup.done.backToList')}</Button>
          </Flex>
        )}
      </Box>
      {phoneFooter}
    </VStack>
  );
}
